import * as d3 from 'd3';

// Same palette used by the Sunburst chart, kept here as a sensible default
// so bars still render in color if the caller never calls .barColors(...).
let defaultSwatches = ['#7096D1','#50A240','#B88932','#DBABD6','#6ED0D8','#85933B','#DEB2A6','#988D67','#9D87D1','#CB79CC','#69D38B','#B3BFD6','#D474A1','#599C6D','#C6B843','#DDBB78','#CA855D','#5AA9CD','#9C8595','#9EC552','#ADC888','#64D2B5','#D07D80','#B5C3AB','#639692'];

function radialBarChart() {

    // Configurable variables (same as before)
    let margin = {top: 20, right: 20, bottom: 20, left: 20};
    let barHeight = 100;
    let reverseLayerOrder = false;
    let barColors = undefined;
    let capitalizeLabels = false;
    let domain = [0, 100];
    let tickValues = undefined;
    let colorLabels = false;
    let tickCircleValues = [];
    let transitionDuration = 1000;
    let onClickCallback = undefined;

    // Scales & other useful things
    let numBars = null;
    let barScale = null;
    let keys = null;
    let labelRadius = 0;

    function init(d) {
        barScale = d3.scaleLinear().domain(domain).range([0, barHeight]);

        let mapData = new Map(Object.entries(d[0].data));
        keys = Array.from(mapData.keys());
        numBars = keys.length;

        // Radius of the key labels
        labelRadius = barHeight * 1.025;
    }

    function svgRotate(a) {
        return `rotate(${+a})`;
    }

    function svgTranslate(x, y) {
        return `translate(${+x},${+y})`;
    }

    function initChart(container) {
        let svg = d3.select(container)
            .append('svg')
            .style('width', 2 * barHeight + margin.left + margin.right + 'px')
            .style('height', 2 * barHeight + margin.top + margin.bottom + 'px')
            .append('g')
            .classed('radial-barchart', true)
            .attr('transform', svgTranslate(margin.left + barHeight, margin.top + barHeight));

        svg.append('g')
            .classed('tick-circles', true)
            .selectAll('circle')
            .data(tickCircleValues)
            .enter()
            .append('circle')
            .attr('r', function(d) { return barScale(d); })
            .style('fill', 'none');

        // Group that will hold the actual bars, kept separate so re-renders
        // only touch this group rather than re-appending overlays.
        svg.append('g')
            .classed('bars', true);

        renderOverlays(container);
    }

    function renderOverlays(container) {
        let g = d3.select(container).select('svg g.radial-barchart');

        // Spokes
        g.append('g')
            .classed('spokes', true)
            .selectAll('line')
            .data(keys)
            .enter()
            .append('line')
            .attr('y2', -barHeight)
            .attr('transform', function(d, i) { return svgRotate(i * 360 / numBars); });

        // Outer circle
        g.append('circle')
            .attr('r', barHeight)
            .classed('outer', true)
            .style('fill', 'none');

        // Labels
        let labels = g.append('g')
            .classed('labels', true);

        labels.append('defs')
            .append('path')
            .attr('id', 'label-path')
            .attr('d', `m0 ${-labelRadius} a${labelRadius} ${labelRadius} 0 1,1 -0.01 0`);

        labels.selectAll('text')
            .data(keys)
            .enter()
            .append('text')
            .style('text-anchor', 'middle')
            .style('fill', function(d, i) {
                if (!colorLabels) return null;
                let palette = barColors || defaultSwatches;
                return palette[i % palette.length];
            })
            .append('textPath')
            .attr('xlink:href', '#label-path')
            .attr('startOffset', function(d, i) { return `${i * 100 / numBars + 50 / numBars}%`; })
            .text(function(d) { return capitalizeLabels ? d.toUpperCase() : d; });
    }

    /* Arc functions */
    let or = function(d, i) {
        return barScale(Math.min(d, domain[1]));
    };
    let sa = function(d, i) {
        return (i * 2 * Math.PI) / numBars;
    };
    let ea = function(d, i) {
        return ((i + 1) * 2 * Math.PI) / numBars;
    };

    function renderBars(container, d) {
        let layerData = reverseLayerOrder ? d.slice().reverse() : d;

        // Current implementation draws the first (or, if reversed, last)
        // layer's values as a single ring of bars, keyed in the same
        // order as `keys` so the general update pattern below keeps each
        // bar matched to its label across re-renders.
        let mapData = new Map(Object.entries(layerData[0].data));
        let values = keys.map(function(k) { return mapData.get(k); });

        let barsGroup = d3.select(container).select('svg g.radial-barchart g.bars');

        let barArc = d3.arc()
            .innerRadius(0)
            .outerRadius(or)
            .startAngle(sa)
            .endAngle(ea);

        let bars = barsGroup.selectAll('path.bar')
            .data(values);

        bars.exit().remove();

        let barsEnter = bars.enter()
            .append('path')
            .classed('bar', true)
            .attr('fill', function(dd, i) {
                let palette = barColors || defaultSwatches;
                return palette[i % palette.length];
            });

        barsEnter.merge(bars)
            .each(function(dd, i) {
                d3.select(this).on('click', function() {
                    if (onClickCallback) {
                        onClickCallback(dd, i);
                    }
                });
            })
            .transition()
            .duration(transitionDuration)
            .attr('d', barArc);
    }

    function chart(selection) {
        selection.each(function(d) {
            init(d);

            let container = this;

            if (d3.select(container).select('svg').empty()) {
                initChart(container);
            }

            renderBars(container, d);
        });
    }

    /* Configuration getters/setters */
    chart.margin = function(_) {
        if (!arguments.length) return margin;
        margin = _;
        return chart;
    };

    chart.barHeight = function(_) {
        if (!arguments.length) return barHeight;
        barHeight = _;
        return chart;
    };

    chart.reverseLayerOrder = function(_) {
        if (!arguments.length) return reverseLayerOrder;
        reverseLayerOrder = _;
        return chart;
    };

    chart.barColors = function(_) {
        if (!arguments.length) return barColors;
        barColors = _;
        return chart;
    };

    chart.capitalizeLabels = function(_) {
        if (!arguments.length) return capitalizeLabels;
        capitalizeLabels = _;
        return chart;
    };

    chart.domain = function(_) {
        if (!arguments.length) return domain;
        domain = _;
        return chart;
    };

    chart.tickValues = function(_) {
        if (!arguments.length) return tickValues;
        tickValues = _;
        return chart;
    };

    chart.colorLabels = function(_) {
        if (!arguments.length) return colorLabels;
        colorLabels = _;
        return chart;
    };

    chart.tickCircleValues = function(_) {
        if (!arguments.length) return tickCircleValues;
        tickCircleValues = _;
        return chart;
    };

    chart.transitionDuration = function(_) {
        if (!arguments.length) return transitionDuration;
        transitionDuration = _;
        return chart;
    };

    chart.onClickCallback = function(_) {
        if (!arguments.length) return onClickCallback;
        onClickCallback = _;
        return chart;
    };

    return chart;
}

export default radialBarChart;